import { useId, useState } from 'react'
import type { ReactNode } from 'react'

import { datum, zahlLesen, zahlText } from '../format'
import Diagramm from './Diagramm'
import type { UiAuswahl, UiBlockDaten, UiDiagramm, UiFeld, UiFormular, UiTabelle, UiZelle } from '../typen'

/**
 * Eingebettete Oberfläche des Assistenten: Tabelle, Diagramm, Formular, Auswahl.
 *
 * Der Assistent schreibt einen Block ```ui mit einem JSON-Objekt in seinen Text; `uiBlockLesen`
 * prüft ihn, `UiBlock` zeigt ihn. Formular und Auswahl schicken beim Klick eine Nutzernachricht in
 * Klartext über denselben Weg wie die Eingabezeile - gebucht wird hier nichts, die Freigabe bleibt
 * an der Werkzeugkarte. Damit ein Formular nach dem Neuladen nicht wieder leer dasteht, sieht die
 * Komponente in den späteren Nutzernachrichten nach, ob es schon beantwortet wurde.
 */

// --- Lesen und Prüfen -----------------------------------------------------------------------------

const FELDTYPEN = ['text', 'zahl', 'datum', 'auswahl', 'schalter'] as const

function istZelle(wert: unknown): wert is UiZelle {
  return wert === null || ['string', 'number', 'boolean'].includes(typeof wert)
}

function istTextListe(wert: unknown): wert is string[] {
  return Array.isArray(wert) && wert.every((x) => typeof x === 'string')
}

function istObjekt(wert: unknown): wert is Record<string, unknown> {
  return typeof wert === 'object' && wert !== null && !Array.isArray(wert)
}

/** Ein UI-Block aus dem JSON-Text; null, wenn er nicht stimmt - dann bleibt er ein Codeblock. */
export function uiBlockLesen(json: string): UiBlockDaten | null {
  let roh: unknown
  try {
    roh = JSON.parse(json)
  } catch {
    return null
  }
  if (!istObjekt(roh)) return null
  const o = roh
  switch (o.art) {
    case 'tabelle': {
      if (!istTextListe(o.spalten) || o.spalten.length === 0) return null
      if (!Array.isArray(o.zeilen) || !o.zeilen.every((z) => Array.isArray(z) && z.every(istZelle))) return null
      if (o.summen !== undefined && !(Array.isArray(o.summen) && o.summen.every(istZelle))) return null
      if (o.einheiten !== undefined && !(istObjekt(o.einheiten) && Object.values(o.einheiten).every((e) => typeof e === 'string'))) return null
      return o as unknown as UiTabelle
    }
    case 'diagramm': {
      if (o.typ !== 'balken' && o.typ !== 'linie') return null
      if (!istTextListe(o.x) || o.x.length === 0) return null
      if (!Array.isArray(o.reihen) || o.reihen.length === 0) return null
      const reihenOk = o.reihen.every(
        (r) => istObjekt(r) && typeof r.name === 'string' && Array.isArray(r.werte) && r.werte.every((w) => w === null || typeof w === 'number'),
      )
      if (!reihenOk) return null
      if (o.einheit !== undefined && typeof o.einheit !== 'string') return null
      return o as unknown as UiDiagramm
    }
    case 'formular': {
      if (typeof o.titel !== 'string' || !Array.isArray(o.felder) || o.felder.length === 0) return null
      const felderOk = o.felder.every(
        (f) =>
          istObjekt(f) &&
          typeof f.name === 'string' &&
          typeof f.beschriftung === 'string' &&
          (FELDTYPEN as readonly string[]).includes(String(f.typ)) &&
          (f.optionen === undefined || istTextListe(f.optionen)) &&
          (f.wert === undefined || istZelle(f.wert)),
      )
      if (!felderOk) return null
      if (o.senden !== undefined && typeof o.senden !== 'string') return null
      return o as unknown as UiFormular
    }
    case 'auswahl': {
      if (typeof o.frage !== 'string' || !Array.isArray(o.optionen) || o.optionen.length === 0) return null
      const ok = o.optionen.every((p) => istObjekt(p) && typeof p.text === 'string' && (p.antwort === undefined || typeof p.antwort === 'string'))
      return ok ? (o as unknown as UiAuswahl) : null
    }
    default:
      return null
  }
}

// --- Gemeinsames ------------------------------------------------------------------------------------

const zahlFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 })

function zelleText(wert: UiZelle, einheit?: string): string {
  if (wert === null || wert === undefined) return ''
  if (typeof wert === 'number') return einheit ? `${zahlFormat.format(wert)} ${einheit}` : zahlFormat.format(wert)
  if (typeof wert === 'boolean') return wert ? 'ja' : 'nein'
  return wert
}

/** Der Text der Antwort einer Option: `antwort`, sonst der Knopftext. */
function optionAntwort(option: UiAuswahl['optionen'][number]): string {
  return option.antwort ?? option.text
}

/** Die Nutzernachricht, die ein abgeschicktes Formular erzeugt. */
function formularNachricht(titel: string, werte: [string, string][]): string {
  return `Formular „${titel}“: ${werte.map(([beschriftung, wert]) => `${beschriftung} = ${wert}`).join('; ')}`
}

export interface UiBlockEigenschaften {
  daten: UiBlockDaten
  /** Schickt eine Nutzernachricht in den Chat - fehlt es, sind Formular und Auswahl nur Anzeige. */
  senden?: (text: string) => void
  /** Während der Assistent arbeitet oder eine Freigabe wartet, ist nichts klickbar. */
  gesperrt?: boolean
  /** Die Texte der Nutzernachrichten nach dieser Antwort - daran erkennt der Block, ob er schon beantwortet ist. */
  spaetereAntworten?: string[]
}

export default function UiBlock({ daten, senden, gesperrt = false, spaetereAntworten = [] }: UiBlockEigenschaften): ReactNode {
  switch (daten.art) {
    case 'tabelle':
      return <Tabelle daten={daten} />
    case 'diagramm':
      // Linien zeigen Verläufe (Preise um 1.450 €/t) - ab null wäre jede flach. Mehrere Linien mit Zahlen an
      // jeder Marke überdecken sich; dann stehen die Werte im Tooltip.
      return (
        <Diagramm
          typ={daten.typ}
          titel={daten.titel}
          x={daten.x}
          reihen={daten.reihen}
          einheit={daten.einheit}
          abNull={daten.typ === 'balken'}
          werteZeigen={daten.typ === 'balken' || daten.reihen.length === 1}
        />
      )
    case 'formular':
      return <Formular daten={daten} senden={senden} gesperrt={gesperrt} spaetereAntworten={spaetereAntworten} />
    case 'auswahl':
      return <Auswahl daten={daten} senden={senden} gesperrt={gesperrt} spaetereAntworten={spaetereAntworten} />
  }
}

// --- Tabelle --------------------------------------------------------------------------------------------

function Tabelle({ daten }: { daten: UiTabelle }) {
  const [kopiert, setKopiert] = useState(false)
  const einheit = (spalte: number) => daten.einheiten?.[daten.spalten[spalte]]
  const zeilen = [...daten.zeilen, ...(daten.summen ? [daten.summen] : [])]
  const istZahlSpalte = daten.spalten.map((_, s) => daten.zeilen.some((z) => typeof z[s] === 'number'))

  function kopieren() {
    const text = [daten.spalten, ...zeilen].map((z, r) => z.map((zelle, s) => (r === 0 ? String(zelle) : zelleText(zelle as UiZelle, einheit(s)))).join('\t')).join('\n')
    void navigator.clipboard.writeText(text).then(() => {
      setKopiert(true)
      window.setTimeout(() => setKopiert(false), 2000)
    })
  }

  return (
    <div className="ui-block ui-tabelle">
      <div className="ui-kopf">
        {daten.titel && <h4>{daten.titel}</h4>}
        <button type="button" className="leise" onClick={kopieren}>
          {kopiert ? 'Kopiert' : 'Kopieren'}
        </button>
      </div>
      <div className="tabellenrahmen chat-tabelle">
        <table>
          <thead>
            <tr>
              {daten.spalten.map((s, i) => (
                <th key={i} className={istZahlSpalte[i] ? 'zahl' : ''}>
                  {s}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {zeilen.map((zeile, r) => {
              const summe = daten.summen !== undefined && r === zeilen.length - 1
              return (
                <tr key={r} className={summe ? 'ui-summe' : ''}>
                  {daten.spalten.map((_, s) => (
                    <td key={s} className={istZahlSpalte[s] ? 'zahl' : ''}>
                      {zelleText(zeile[s] ?? null, einheit(s))}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// --- Formular --------------------------------------------------------------------------------------------

type Formularwerte = Record<string, string | boolean>

function anfangswert(feld: UiFeld): string | boolean {
  const w = feld.wert
  if (feld.typ === 'schalter') return w === true || w === 'ja' || w === 'true'
  if (w === null || w === undefined) return ''
  if (feld.typ === 'zahl') return typeof w === 'number' ? zahlText(w) : String(w)
  if (feld.typ === 'datum') {
    const de = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(String(w))
    return de ? `${de[3]}-${de[2]}-${de[1]}` : String(w).slice(0, 10)
  }
  return String(w)
}

/** Aus einer früheren Nutzernachricht „Formular „Titel“: A = x; B = y" die Paare zurücklesen. */
function gesendeteWerte(titel: string, spaetere: string[]): [string, string][] | null {
  const anfang = `Formular „${titel}“: `
  const treffer = spaetere.find((t) => t.startsWith(anfang))
  if (!treffer) return null
  return treffer
    .slice(anfang.length)
    .split('; ')
    .map((paar) => {
      const i = paar.indexOf(' = ')
      return i < 0 ? [paar, ''] : [paar.slice(0, i), paar.slice(i + 3)]
    })
}

function Formular({ daten, senden, gesperrt, spaetereAntworten }: { daten: UiFormular } & Required<Omit<UiBlockEigenschaften, 'daten' | 'senden'>> & Pick<UiBlockEigenschaften, 'senden'>) {
  const [werte, setWerte] = useState<Formularwerte>(() => Object.fromEntries(daten.felder.map((f) => [f.name, anfangswert(f)])))
  const [fehler, setFehler] = useState<string | null>(null)
  const [gesendet, setGesendet] = useState<[string, string][] | null>(null)
  const kennungBasis = useId()
  const fertig = gesendet ?? gesendeteWerte(daten.titel, spaetereAntworten)

  function absenden() {
    const paare: [string, string][] = []
    for (const f of daten.felder) {
      const roh = werte[f.name]
      let text: string
      if (f.typ === 'schalter') text = roh === true ? 'ja' : 'nein'
      else if (f.typ === 'zahl') {
        const eingabe = String(roh ?? '').trim()
        if (eingabe === '') text = ''
        else {
          const zahl = zahlLesen(eingabe)
          if (zahl === null) {
            setFehler(`${f.beschriftung}: „${eingabe}“ ist keine Zahl.`)
            return
          }
          text = zahlFormat.format(zahl)
        }
      } else if (f.typ === 'datum') text = roh ? datum(String(roh)) : ''
      else text = String(roh ?? '').trim()
      if (text === '' && f.pflicht) {
        setFehler(`${f.beschriftung} fehlt.`)
        return
      }
      if (text !== '' && f.einheit && f.typ === 'zahl') text = `${text} ${f.einheit}`
      paare.push([f.beschriftung, text === '' ? '(leer)' : text])
    }
    setFehler(null)
    setGesendet(paare)
    senden?.(formularNachricht(daten.titel, paare))
  }

  if (fertig) {
    return (
      <div className="ui-block ui-formular gesendet">
        <h4>
          {daten.titel} <span className="hinweis">- gesendet</span>
        </h4>
        <dl>
          {fertig.map(([beschriftung, wert]) => (
            <div key={beschriftung}>
              <dt>{beschriftung}</dt>
              <dd>{wert}</dd>
            </div>
          ))}
        </dl>
      </div>
    )
  }

  const aus = gesperrt || !senden
  return (
    <form
      className="ui-block ui-formular"
      onSubmit={(ev) => {
        ev.preventDefault()
        absenden()
      }}
    >
      <h4>{daten.titel}</h4>
      <div className="ui-felder">
        {daten.felder.map((f) => {
          const kennung = `${kennungBasis}${f.name}`
          const wert = werte[f.name]
          const setzen = (neu: string | boolean) => setWerte((alt) => ({ ...alt, [f.name]: neu }))
          const beschriftung = (
            <span className="hinweis">
              {f.beschriftung}
              {f.einheit ? ` (${f.einheit})` : ''}
              {f.pflicht ? ' *' : ''}
            </span>
          )
          if (f.typ === 'schalter') {
            return (
              <label key={f.name} className="feld ui-schalter">
                {beschriftung}
                <span>
                  <input type="checkbox" checked={wert === true} disabled={aus} onChange={(ev) => setzen(ev.target.checked)} /> {wert === true ? 'ja' : 'nein'}
                </span>
              </label>
            )
          }
          if (f.typ === 'auswahl') {
            return (
              <label key={f.name} className="feld" htmlFor={kennung}>
                {beschriftung}
                <select id={kennung} value={String(wert ?? '')} disabled={aus} onChange={(ev) => setzen(ev.target.value)}>
                  <option value="">- bitte wählen -</option>
                  {(f.optionen ?? []).map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </label>
            )
          }
          return (
            <label key={f.name} className="feld" htmlFor={kennung}>
              {beschriftung}
              <input
                id={kennung}
                type={f.typ === 'datum' ? 'date' : 'text'}
                inputMode={f.typ === 'zahl' ? 'decimal' : undefined}
                value={String(wert ?? '')}
                disabled={aus}
                onChange={(ev) => setzen(ev.target.value)}
              />
            </label>
          )
        })}
      </div>
      {fehler && <p className="ui-fehler">{fehler}</p>}
      <div className="leiste" style={{ marginBottom: 0 }}>
        <button type="submit" className="haupt" disabled={aus}>
          {daten.senden ?? 'Senden'}
        </button>
        <span className="hinweis">Schickt die Werte als Nachricht an den Assistenten; gebucht wird erst nach Freigabe.</span>
      </div>
    </form>
  )
}

// --- Auswahl --------------------------------------------------------------------------------------------

function Auswahl({ daten, senden, gesperrt, spaetereAntworten }: { daten: UiAuswahl } & Required<Omit<UiBlockEigenschaften, 'daten' | 'senden'>> & Pick<UiBlockEigenschaften, 'senden'>) {
  const [gewaehlt, setGewaehlt] = useState<number | null>(null)
  const frueher = spaetereAntworten.length > 0 ? daten.optionen.findIndex((o) => spaetereAntworten.includes(optionAntwort(o))) : -1
  const wahl = gewaehlt ?? (frueher >= 0 ? frueher : null)
  const aus = gesperrt || !senden || wahl !== null

  return (
    <div className={`ui-block ui-auswahl${wahl !== null ? ' gesendet' : ''}`}>
      <p>{daten.frage}</p>
      <div className="leiste" style={{ marginBottom: 0 }}>
        {daten.optionen.map((o, i) => (
          <button
            key={i}
            type="button"
            className={wahl === i ? 'haupt ui-gewaehlt' : ''}
            disabled={aus}
            onClick={() => {
              setGewaehlt(i)
              senden?.(optionAntwort(o))
            }}
          >
            {o.text}
            {wahl === i ? ' - gewählt' : ''}
          </button>
        ))}
      </div>
    </div>
  )
}
